/**
 * V2 admin · Create Tenant (Admin or Super Admin)
 *
 * Manually creates a tenant profile + booking without requiring the
 * automated booking, signature, or invitation workflow.
 *
 * Step 1: Create auth user (via admin-invite-user edge function, skip email)
 * Step 2: Insert booking record linking user → room with the given dates/status
 */
import { useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, UserPlus } from "lucide-react";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { buildCreateTenantInviteBody } from "@/lib/create-tenant-invite";
import { useAuth } from "@/contexts/AuthContext";
import {
  useProperties,
  usePropertyRooms,
  useCreateBooking,
} from "@/hooks/v2/useProperties";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";

function addSixMonths(dateStr: string): string {
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + 6);
  return d.toISOString().slice(0, 10);
}

/**
 * `bookings.status` is a CLAIM, not evidence — the journey stage is derived
 * from what is actually on file (contract → deposit → keys → move-in). Picking
 * "Active" here does not make a tenant Active; it only makes the profile show
 * "Booking status says Active, but the tenant is at Applied" until the earlier
 * steps are fulfilled. Each label says what the status means so an admin picks
 * where the tenant really is.
 *
 * The default is `pending` (= Applied): a tenant created here has no contract,
 * no deposit and no keys yet. It used to default to `active`, which is why
 * manually-created tenants all carried a status ahead of their real stage.
 */
function getBookingStatuses(t: (key: string, fallback: string) => string) {
  return [
    {
      value: "draft",
      label: t("admin.createTenant.statusDraft", "Draft — not applied yet"),
    },
    {
      value: "pending",
      label: t(
        "admin.createTenant.statusPending",
        "Pending — applied, nothing signed yet"
      ),
    },
    {
      value: "confirmed",
      label: t(
        "admin.createTenant.statusConfirmed",
        "Confirmed — contract signed"
      ),
    },
    {
      value: "approved",
      label: t(
        "admin.createTenant.statusApproved",
        "Approved — contract approved"
      ),
    },
    {
      value: "active",
      label: t(
        "admin.createTenant.statusActive",
        "Active — already moved in (back-fill)"
      ),
    },
  ];
}

export default function CreateTenantPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isSuperAdmin, hasSalesAdminRole, user } = useAuth();
  const canCreateTenant = isSuperAdmin || hasSalesAdminRole;
  const BOOKING_STATUSES = getBookingStatuses(t);

  const { data: properties = [] } = useProperties();
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(
    null
  );
  const { data: rooms = [] } = usePropertyRooms(selectedPropertyId);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    propertyId: "",
    roomId: "",
    moveInDate: "",
    moveOutDate: "",
    // Applied — see BOOKING_STATUSES. A brand-new tenant has nothing on file.
    status: "pending",
    monthlyRent: "",
    depositAmount: "",
    notes: "",
  });

  const [busy, setBusy] = useState(false);
  const createBooking = useCreateBooking();

  // What the last room selection auto-filled into monthlyRent/depositAmount,
  // so picking a DIFFERENT room afterwards can tell "still the previous
  // room's autofill" (refresh it) apart from "the admin typed their own
  // figure" (keep it) — a field that's merely non-empty can't make that
  // distinction on its own.
  const autoFilledRef = useRef<{ rent: string | null; deposit: string | null }>(
    { rent: null, deposit: null }
  );

  // Guard — must come after all hooks
  if (!canCreateTenant) {
    return (
      <AdminV2Layout
        title={t("admin.createTenant.accessDenied", "Access denied")}
        breadcrumbs={[
          {
            label: t("admin.createTenant.breadcrumbAdmin", "Admin"),
            to: "/admin",
          },
        ]}
      >
        <Card className="p-8 max-w-md">
          <p className="text-sm text-muted-foreground">
            {t(
              "admin.createTenant.onlyAdmins",
              "Only Admins can manually create tenants."
            )}
          </p>
          <Button asChild variant="outline" size="sm" className="mt-4">
            <Link to="/admin/properties">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("admin.createTenant.back", "Back")}
            </Link>
          </Button>
        </Card>
      </AdminV2Layout>
    );
  }

  function set(key: keyof typeof form, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function validate(): string | null {
    if (!form.firstName.trim())
      return t("admin.createTenant.errFirstName", "First name is required.");
    if (!form.lastName.trim())
      return t("admin.createTenant.errLastName", "Last name is required.");
    if (!form.email.trim() || !form.email.includes("@"))
      return t("admin.createTenant.errEmail", "Valid email is required.");
    if (!form.roomId)
      return t("admin.createTenant.errRoom", "Room is required.");
    if (!form.moveInDate)
      return t("admin.createTenant.errMoveIn", "Move-in date is required.");
    if (!form.status)
      return t("admin.createTenant.errStatus", "Tenant status is required.");
    if (
      form.moveOutDate &&
      form.moveInDate &&
      form.moveOutDate < addSixMonths(form.moveInDate)
    ) {
      return t(
        "admin.createTenant.errMoveOut",
        "Earliest possible move-out must be at least 6 months after move-in date."
      );
    }
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    if (!user) return;

    setBusy(true);
    try {
      // Step 1: Create auth user (skip invite email — super admin will send separately)
      const { data: inviteData, error: inviteError } =
        await supabase.functions.invoke<{
          ok?: boolean;
          user_id?: string;
          existed?: boolean;
          error?: string;
        }>("admin-invite-user", {
          // Built in one place so `preserve_existing_role` stays on — see
          // create-tenant-invite.ts for why dropping it deletes an existing
          // owner's role and property assignments.
          body: buildCreateTenantInviteBody({
            email: form.email,
            firstName: form.firstName,
            lastName: form.lastName,
            phone: form.phone,
          }),
        });

      if (inviteError || inviteData?.error || !inviteData?.user_id) {
        throw new Error(
          inviteData?.error ??
            inviteError?.message ??
            t(
              "admin.createTenant.errCreateAccount",
              "Failed to create user account."
            )
        );
      }

      const newUserId = inviteData.user_id;
      /**
       * The email resolved to an account that already existed, so this form
       * created a booking for someone already in the system rather than a new
       * tenant. `preserve_existing_role` above left their profile untouched on
       * purpose, and the writes below honour the same rule.
       */
      const reusedExistingAccount = inviteData.existed === true;

      // Update phone if provided (admin-invite-user doesn't persist phone).
      // Skipped for an account that already existed: the number on file is
      // theirs, and this form's field is blank-by-default, so writing it would
      // let a booking quietly overwrite an owner's real contact details.
      if (form.phone.trim() && !reusedExistingAccount) {
        await supabase
          .from("profiles")
          .update({ phone: form.phone.trim() })
          .eq("id", newUserId);
      }

      // Step 2: Create booking
      const booking = await createBooking.mutateAsync({
        input: {
          room_id: Number(form.roomId),
          user_id: newUserId,
          status: form.status,
          move_in_date: form.moveInDate,
          // The form date is the contract's EARLIEST POSSIBLE move-out
          // (contracts auto-prolong), so it belongs in end_date only.
          // move_out_date stays NULL until a real move-out is confirmed in
          // the tenant Move-out tab — the automated move-out email chains
          // key on it (COL-304 + protocol reminders).
          move_out_date: null,
          start_date: form.moveInDate,
          end_date: form.moveOutDate || addSixMonths(form.moveInDate),
          monthly_rent: form.monthlyRent ? Number(form.monthlyRent) : null,
          deposit_amount: form.depositAmount
            ? Number(form.depositAmount)
            : null,
          // Persist tenant identity on the booking so room/apartment views show
          // the right name/email even when the profile join is unavailable (COL-261).
          name:
            `${form.firstName.trim()} ${form.lastName.trim()}`.trim() || null,
          email: form.email.trim().toLowerCase() || null,
        },
        editorId: user.id,
      });

      toast.success(
        reusedExistingAccount
          ? t(
              "admin.createTenant.createdForExisting",
              "Booking created for the existing account with this email. Its login and roles were left unchanged."
            )
          : t(
              "admin.createTenant.createdSuccess",
              "Tenant created successfully. Send a login link when ready."
            )
      );
      navigate(
        `/admin/properties/${form.propertyId}/rooms/${form.roomId}/tenants/${booking.id}`
      );
    } catch (err: unknown) {
      toast.error(
        err instanceof Error
          ? err.message
          : t("admin.createTenant.errCreateTenant", "Failed to create tenant.")
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminV2Layout
      title={t("admin.createTenant.pageTitle", "Create tenant")}
      breadcrumbs={[
        {
          label: t("admin.createTenant.breadcrumbAdmin", "Admin"),
          to: "/admin",
        },
        {
          label: t("admin.createTenant.breadcrumbProperties", "Properties"),
          to: "/admin/properties",
        },
        { label: t("admin.createTenant.pageTitle", "Create tenant") },
      ]}
    >
      <div className="max-w-2xl">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
              <UserPlus className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold">
                {t("admin.createTenant.newTenant", "New tenant")}
              </h2>
              <p className="text-xs text-muted-foreground">
                {t(
                  "admin.createTenant.newTenantDesc",
                  "Manually create a tenant profile and booking. No automated flow or invitation required."
                )}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Personal info */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                {t("admin.createTenant.personalInfo", "Personal information")}
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="firstName">
                    {t("admin.createTenant.firstName", "First name *")}
                  </Label>
                  <Input
                    id="firstName"
                    value={form.firstName}
                    onChange={(e) => set("firstName", e.target.value)}
                    placeholder="Anna"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lastName">
                    {t("admin.createTenant.lastName", "Last name *")}
                  </Label>
                  <Input
                    id="lastName"
                    value={form.lastName}
                    onChange={(e) => set("lastName", e.target.value)}
                    placeholder="Müller"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email">
                    {t("admin.createTenant.email", "Email *")}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="anna@example.com"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="phone">
                    {t("admin.createTenant.phone", "Phone")}
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    placeholder="+49 170 1234567"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Room assignment */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                {t("admin.createTenant.roomAssignment", "Room assignment")}
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>
                    {t("admin.createTenant.property", "Property *")}
                  </Label>
                  <Select
                    value={form.propertyId}
                    onValueChange={(v) => {
                      set("propertyId", v);
                      set("roomId", "");
                      setSelectedPropertyId(Number(v));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t(
                          "admin.createTenant.selectProperty",
                          "Select property…"
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>
                          {p.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("admin.createTenant.room", "Room *")}</Label>
                  <Select
                    value={form.roomId}
                    onValueChange={(v) => {
                      set("roomId", v);
                      // The room is the source of truth for base rent and
                      // deposit (COL-360): start from its figures so a new
                      // tenant is not created already out of step with the
                      // room. Only fills a field that's empty OR still holds
                      // the PREVIOUS room's autofill — a figure the admin
                      // typed themselves is theirs to keep, but switching the
                      // room again must refresh what we filled in, not leave
                      // the old room's numbers under the new room's name.
                      const picked = rooms.find((r) => String(r.id) === v) as
                        | {
                            price?: number | null;
                            deposit_amount?: number | null;
                          }
                        | undefined;
                      if (!picked) return;
                      setForm((f) => {
                        const next = { ...f };
                        const price = Number(picked.price);
                        const nextRent = price > 0 ? String(price) : "";
                        const rentIsAutofillOrEmpty =
                          f.monthlyRent === "" ||
                          f.monthlyRent === autoFilledRef.current.rent;
                        if (rentIsAutofillOrEmpty) next.monthlyRent = nextRent;
                        autoFilledRef.current.rent = rentIsAutofillOrEmpty
                          ? nextRent
                          : null;

                        const deposit = Number(picked.deposit_amount);
                        const nextDeposit =
                          deposit > 0
                            ? String(deposit)
                            : price > 0
                              ? String(Math.round(price * 2 * 100) / 100)
                              : "";
                        const depositIsAutofillOrEmpty =
                          f.depositAmount === "" ||
                          f.depositAmount === autoFilledRef.current.deposit;
                        if (depositIsAutofillOrEmpty)
                          next.depositAmount = nextDeposit;
                        autoFilledRef.current.deposit = depositIsAutofillOrEmpty
                          ? nextDeposit
                          : null;

                        return next;
                      });
                    }}
                    disabled={!form.propertyId}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          form.propertyId
                            ? t("admin.createTenant.selectRoom", "Select room…")
                            : t(
                                "admin.createTenant.selectPropertyFirst",
                                "Select property first"
                              )
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {rooms.map((r) => (
                        <SelectItem key={r.id} value={String(r.id)}>
                          {r.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            {/* Dates & status */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                {t("admin.createTenant.tenancyDetails", "Tenancy details")}
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="moveInDate">
                    {t("admin.createTenant.moveInDate", "Move-in date *")}
                  </Label>
                  <Input
                    id="moveInDate"
                    type="date"
                    value={form.moveInDate}
                    onChange={(e) => {
                      const d = e.target.value;
                      setForm((f) => ({
                        ...f,
                        moveInDate: d,
                        moveOutDate: d ? addSixMonths(d) : f.moveOutDate,
                      }));
                    }}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="moveOutDate">
                    {t(
                      "admin.createTenant.earliestMoveOut",
                      "Earliest possible move-out"
                    )}
                  </Label>
                  <Input
                    id="moveOutDate"
                    type="date"
                    value={form.moveOutDate}
                    onChange={(e) => set("moveOutDate", e.target.value)}
                    min={
                      form.moveInDate
                        ? addSixMonths(form.moveInDate)
                        : undefined
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>
                    {t("admin.createTenant.tenantStatus", "Tenant status *")}
                  </Label>
                  <Select
                    value={form.status}
                    onValueChange={(v) => set("status", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BOOKING_STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "admin.createTenant.journeyStageNote",
                      "The journey stage is worked out from what is on file (contract → deposit → keys → move-in). A status ahead of that shows as a warning on the profile instead of promoting the tenant."
                    )}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="monthlyRent">
                    {t("admin.createTenant.monthlyRent", "Monthly rent (€)")}
                  </Label>
                  <Input
                    id="monthlyRent"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.monthlyRent}
                    onChange={(e) => set("monthlyRent", e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="depositAmount">
                    {t("admin.createTenant.deposit", "Deposit (€)")}
                  </Label>
                  <Input
                    id="depositAmount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.depositAmount}
                    onChange={(e) => set("depositAmount", e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div className="flex items-center justify-between pt-1">
              <p className="text-xs text-muted-foreground">
                {t(
                  "admin.createTenant.loginLinkNote",
                  "A login link can be sent after creation via the user management panel."
                )}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  disabled={busy}
                >
                  {t("admin.createTenant.cancel", "Cancel")}
                </Button>
                <Button type="submit" disabled={busy}>
                  {busy
                    ? t("admin.createTenant.creating", "Creating…")
                    : t("admin.createTenant.submit", "Create tenant")}
                </Button>
              </div>
            </div>
          </form>
        </Card>
      </div>
    </AdminV2Layout>
  );
}
